<template>

  <div class="exhibitor-detail-dialog" v-if="isOpen">
    <div class="exhibitor-detail-dialog-container" ref="container">
      <div class="close-button">
        <div v-on:click="closeDialog">
          <img src="@/assets/img/xmark.svg"/>
        </div>
      </div>
      <div class="exhibitor-detail-container">
        <div class="left-infos">
          <span class="name">{{ exhibitor.name }}</span>
          <span class="address" v-if="exhibitor.address">{{ exhibitor.address }}</span>
          <span class="plz" v-if="exhibitor.plz || exhibitor.city">{{ exhibitor.plzAndCity }}</span>
          <span class="country" v-if="exhibitor.country">{{ exhibitor.country }}</span>

          <span class="phone" v-if="exhibitor.phone">
            <i class="fas fa-phone"></i> {{ exhibitor.phone }}
          </span>
          <div class="web" v-if="exhibitor.web">
            <i class="fas fa-globe"></i> <a target="_blank" :href="exhibitor.webWithProtocol">{{ exhibitor.web }}</a>
          </div>
          <div class="email" v-if="exhibitor.email">
            <i class="fas fa-envelope"></i> <a target="_blank" :href="'mailto:'+ exhibitor.email">{{
              exhibitor.email
            }}</a>
          </div>
          <div class="socials" v-if="exhibitor.socials">
            <div class="social" v-for="social of exhibitor.socials">
              <i class="fa-brands" v-bind:class="getIconBySocialType(social.type)"></i> <a target="_blank" :href="social.urlWithProtocol">{{ social.url }}</a>
            </div>
          </div>
          <div class="stand-container" v-if="exhibitor.stands">
            <i class="fas fa-map-marker-alt"></i>
            <div class="stands">
              <div class="stand" v-for="(stand,index) of exhibitor.stands"
                   v-on:click="callOpenStandDialog(stand,$event)"
                   v-html="(stand.getDisplayName() + (index !== exhibitor.stands.length -1 ? ',' : ''))">
              </div>
            </div>
          </div>
        </div>
        <div :class="exhibitor.logo ? 'exhibitor-logo' : 'exhibitor-logo-letter'">
          <img v-if="exhibitor.logo" :src="exhibitor.getLogoUrl()">
          <span v-else>{{ exhibitor.getFirstLetter() }}</span>
        </div>
      </div>
      <div class="exhibitor-infos">
        <span class="infos" v-if="exhibitor.info" v-html="exhibitor.info"></span>
      </div>
      <div class="footer">
        <template v-if="exhibitor.themesDisplay">
          <theme-root v-for="three of themeTrees" v-bind:root="three.root" v-bind:themes="themes" v-bind:entity-themes="exhibitor.themesDisplay"></theme-root>
        </template>
        <template v-if="exhibitor.products">
          <div class="products-header">{{ $t('products') }}</div>
          <ul>
            <template v-for="product of exhibitor.products">
              <li class="product">
                <div class="product-stand-container">
                  <span class="product-name" v-on:click="openProductDialog(product,$event)">{{ product.name }}</span>
                  <!--                  (-->
                  <!--                  <div class="stand" v-for="(stand,index) of product.stands"-->
                  <!--                       v-on:click="callOpenStandDialog(stand,$event)"-->
                  <!--                       v-html="(stand.getDisplayName() + (index !== product.stands.length -1 ? ',&nbsp;' : ''))">-->
                  <!--                  </div>-->
                  <!--                  )-->
                </div>
              </li>
            </template>
          </ul>
        </template>

      </div>
    </div>
  </div>

</template>

<script lang="ts">
import {Options, Vue} from "vue-class-component";
import {Exhibitor} from "@/entities/Exhibitor";
import {isDefined} from "@/utils";
import Stand from "@/entities/Stand";
import {Theme} from '@/entities/Theme';
import {ThemeTree, ThemeTreeNode} from '@/entities/ThemeTree';
import ThemeRoot from '@/components/ThemeRoot.vue';
import ThemeDisplay from '@/components/ThemeDisplay.vue';


@Options({
  components: {ThemeDisplay, ThemeRoot},
  props: {
    themes: {
      default: [],
      required: true,
      type: Array
    },
    openStandDialog: {
      required: true,
      type: Function
    },
    openProductDialog: {
      required: true,
      type: Function
    }
  }
})
export default class ExhibitorDetailDialog extends Vue {

  private exhibitor: Exhibitor;
  private themes: Theme[];
  public isOpen: boolean = false;
  private openStandDialog: Function;
  private themeTrees: ThemeTree[];

  constructor(props) {
    super(props);
  }

  public openDialog(exhibitor: Exhibitor): void {
    this.exhibitor = exhibitor;
    this.buildThemeTreeForExhibitor();
    this.addClickListener();
    this.isOpen = true;
  }

  private buildThemeTreeForExhibitor() {
    const roots: Theme[] = this.themes.filter(t => t.parentId === '-1' || t.parentId === 'ROOT');
    Theme.sort(roots);
    const themeTrees: ThemeTree[] = [];
    for (const root of roots) {
      const rootChildren = this.themes.filter(t => t.parentId === root.id);
      Theme.sort(rootChildren);
      const themeTree = new ThemeTree(new ThemeTreeNode(root, rootChildren.map(t => new ThemeTreeNode(t, []))));
      this.traverseTree(themeTree.root);
      themeTrees.push(themeTree);
    }
    this.themeTrees = themeTrees;
  }

  public traverseTree(root: ThemeTreeNode) {
    for (const node of root.children) {
      const children = this.themes.filter(t => t.parentId === node.theme.id);
      if (children.length > 0) {
        node.children = children.map(c => new ThemeTreeNode(c, []));
        this.traverseTree(node);
        for (const n of node.children) {
          this.traverseTree(n);
        }
      }
    }
  }

  public closeDialog(): void {
    this.removeClickListener();
    this.isOpen = false;
  }

  private callOpenStandDialog(stand: Stand, event: Event): void {
    this.removeClickListener();
    this.openStandDialog(stand, event)
  }

  private onClick(event) {
    if (isDefined(this.$refs.container) && this.isOpen) {
      const container = this.$refs.container as any;
      if (isDefined(container)) {
        if (!container.contains(event.target)) {
          this.closeDialog();
        }
      }
    }
  }

  public addClickListener() {
    window.addEventListener('click', this.onClick);
  }

  private removeClickListener() {
    window.removeEventListener('click', this.onClick);
  }

  public getIconBySocialType(type: string): string {
    if (isDefined(type)) {
      switch (type) {
        case 'I':
          return 'fa-instagram';
        case 'F' :
          return 'fa-facebook'
        case 'L' :
          return 'fa-linkedin'
        case 'T' :
          return 'fa-x-twitter'
        case 'X' :
          return 'fa-xing'
        case 'O':
          return 'fa-tiktok'
        case 'Y':
           return 'fa-youtube'
      }
    }
  }

}
</script>

<style scoped lang="scss">

.exhibitor-detail-dialog {
  position: fixed;
  z-index: 9997;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: opacity .3s ease;

  &-container {
    position: relative;
    padding: 64px;
    width: 100%;
    max-width: 1000px;
    max-height: 80vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 auto;
    transition: all .3s ease;
    background: #fff;
    box-shadow: 0 0 100px rgb(0 0 0 / 30%);

    .close-button {
      position: absolute;
      top: 0;
      right: 50px;

      > div {
        width: 50px;
        height: 50px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: white;
        background-color: var(--primary-color);
        font-size: 1.5rem;
        cursor: pointer;
        position: fixed;

        > img {
          width: 25px;
        }
      }
    }

    .exhibitor-detail-container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .left-infos {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        max-width: 75%;

        .name {
          font-size: 2rem;
          line-height: 1.2;
          font-weight: bold;
          text-transform: uppercase;
          margin-bottom: 16px;
          line-break: anywhere;
        }

        .socials {
          display: flex;
          flex-direction: column;
        }

        .plz, .country, .phone, .web, .email, .address, .stand, .social {
          font-size: 1rem;
          text-align: left;
          line-height: 1.5;
          white-space: nowrap;
          overflow: hidden;
          word-break: keep-all;
          text-overflow: ellipsis;
          max-width: 100%;
          display: flex;
          align-items: center;
          margin: 5px 0;

          > i {
            min-width: 26px;
            color: var(--primary-color);
            margin-right: 12px;
            font-size: 1.6rem;
          }
        }

        .stand {
          cursor: pointer;
        }

        .country {
          margin-bottom: 24px;
        }

        .web, .email, .social {
          > a {
            color: #000000;
          }
        }

        .stand-container {
          margin-top: 24px;
          display: flex;
          flex-direction: row;
          align-items: center;

          > i {
            margin-right: 12px;
            font-size: 1.8rem;
            color: var(--primary-color);
          }

          .stands {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;

            .stand {
              font-weight: bold;
              white-space: nowrap;
              word-break: keep-all;
              margin: 0 3px;
            }
          }
        }
      }

      .exhibitor-logo, .exhibitor-logo-letter {
        display: flex;
        justify-content: center;
        width: 20vw;
        height: 20vw;
        max-width: 200px;
        max-height: 200px;
        border: 1px solid rgba(128, 128, 128, 0.5);

        > img {
          max-height: 80%;
          max-width: 80%;
          align-self: center;
        }
      }

      .exhibitor-logo-letter {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #efefef;

        span {
          font-size: 3rem;
          font-weight: bold;
          color: #D3D3D3;
        }
      }

    }

    .exhibitor-infos {
      text-align: left;
      font-size: 1rem;
      line-height: 1.33;
      margin-top: 32px;
    }

    .footer {
      // border-top: 2px solid var(--primary-color);
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      flex-wrap: wrap;


      .products-header {
        text-align: left;
        font-size: 1.2rem;
        font-weight: bold;
        margin-top: 16px;
      }

      .product {
        margin-bottom: 4px;

        .product-stand-container {
          display: flex;
          flex-direction: row;

          .product-name {
            margin-right: 6px;
            font-weight: bold;
            cursor: pointer;
          }

          .stand {
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            word-break: keep-all;
            text-overflow: ellipsis;
            max-width: 100%;
            display: flex;
            align-items: center;
            cursor: pointer;
          }
        }
      }

    }
  }


  @media only screen and (max-width: 766px) {
    &-container {
      padding: 64px 16px 16px 16px;
    }
  }

}

</style>
